import { Task } from '@lit/task';
import { css, html, LitElement } from 'lit';
import { property, queryAsync, state } from 'lit/decorators.js';
import '../components/Button';
import '../components/FileCard';
import '../components/Search';
import '../MediaPickerUploadFile';
import { errorIcon } from '../shared/icons/error';
import { gridIcon } from '../shared/icons/grid';
import { listIcon } from '../shared/icons/list';
import { uploadIcon } from '../shared/icons/upload';
import { MediaFile, ViewMode } from '../types';
import { getApiUrlEndpoints } from '../utils';

//
import type { MediaPickerUploadFile } from '../MediaPickerUploadFile';
import './EmptyList';
import './ErrorLoading';
import './FileCardView';
import './FileListView';
import './LoadingFiles';

export class MediaGallery extends LitElement {
  static readonly styles = css`
    :host {
      font-family: Roboto, sans-serif;

      --background-color: #fff;
      --spacing-vertical: 12px;
      --spacing-horizontal: 24px;

      --xl-cols: 2;
      --lg-cols: 3;
      --md-cols: 4;
      --sm-cols: 6;
    }

    .gallery {
      box-sizing: border-box;
      width: 100%;
    }

    .gallery .gallery-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--spacing-vertical) var(--spacing-horizontal);
      background: var(--background-color);
      position: sticky;
      top: 0;
      z-index: 1;
      height: 64px;
      box-sizing: border-box;
    }

    .gallery header h1 {
      margin: 0;
    }

    .gallery header .actions {
      display: flex;
      gap: 12px;
    }

    .gallery .gallery-body {
      padding: var(--spacing-horizontal);
    }
  `;

  @property({ type: Array })
  selected: MediaFile[] = [];

  @property({ type: Number }) maxFileSelected: number | undefined;

  @state() files: MediaFile[] = [];

  @state() page = 1;

  @state() pageSize = 25;

  @state() viewMode: ViewMode = ViewMode.card;

  @state() searchTerms = '';

  @state()
  selectedFiles: MediaFile[] = [];

  @state()
  showUploadDialog = false;

  @queryAsync('media-picker-upload-file')
  _fileUploadDialog!: Promise<MediaPickerUploadFile>;

  /**
   *
   */
  private get apiUrlEndpoints() {
    return getApiUrlEndpoints();
  }

  private readonly getApiFiles = new Task(
    this,
    () =>
      fetch(
        `${this.apiUrlEndpoints.getFiles}?pagination[start]=${this.page}&pagination[limit]=${this.pageSize}`
      ).then(response => response.json()),
    () => [this.page, this.pageSize]
  );

  /**
   * Toggles view mode between card and list
   */
  private readonly toggleViewMode = () => {
    this.viewMode =
      this.viewMode === ViewMode.card ? ViewMode.list : ViewMode.card;
  };

  /**
   * Handles search input
   */
  private readonly handleSearch = (event: CustomEvent) => {
    this.searchTerms = event.detail.value;
  };

  /**
   * Filters files based on search terms
   */
  private filterFiles(files: MediaFile[]) {
    const searchRegex = new RegExp(this.searchTerms, 'i');
    return files.filter(file => searchRegex.test(file.name));
  }

  private dispatchSelection(files: MediaFile[]) {
    this.dispatchEvent(
      new CustomEvent('selection-changed', {
        detail: {
          files,
        },
      })
    );
  }

  /**
   * Selects a file
   */
  private readonly selectFileCustomEventHandler = (event: CustomEvent) => {
    this.selectedFiles = [...this.selectedFiles, event.detail.file];

    this.dispatchSelection(this.selectedFiles);
  };

  /**
   * Unselects a file
   */
  private readonly unselectFileCustomEventHandler = (event: CustomEvent) => {
    this.selectedFiles = this.selectedFiles.filter(
      f => f.id !== event.detail.file.id
    );

    this.dispatchSelection(this.selectedFiles);
  };

  private readonly isFileSelected = (file: MediaFile) => {
    return this.selectedFiles.some(f => f.id === file.id);
  };

  private readonly canSelect = () => {
    return (
      this.maxFileSelected === undefined ||
      (this.maxFileSelected && this.selectedFiles.length < this.maxFileSelected)
    );
  };

  private readonly openUploadDialog = () => {
    this._fileUploadDialog.then(dialog => dialog.openUploadFileDialog());
  };

  private readonly closeUploadDialog = () => {
    this._fileUploadDialog.then(dialog => dialog.closeUploadFileDialog());
  };

  private readonly handleFileChange = (event: CustomEvent) => {
    const files = event.detail.files;
    if (files) {
    }
  };

  private readonly handleFileUploaded = (event: CustomEvent) => {
    this.dispatchEvent(
      new CustomEvent('files-uploaded', {
        detail: {
          files: event.detail.files,
        },
      })
    );
  };

  private renderLoading() {
    if (this.viewMode === ViewMode.card) {
      return html`<div class="gallery-cards placeholders">
        ${Array.from({ length: this.pageSize }).map(
          () => html`<div class="placeholder gallery-cards__item"></div>`
        )}
        <div class="info">Loading gallery files...</div>
      </div>`;
    }

    if (this.viewMode === ViewMode.list) {
      return html` <p>Loading...</p> `;
    }

    return html` <p>Loading...</p> `;
  }

  /**
   * Renders the error state for loading files
   */
  private renderError() {
    return html` <div class="error-loading">
      ${errorIcon}
      <div class="error">Error loading files</div>
      <media-picker-button
        variant="primary"
        label="Retry"
        @click="${this.getApiFiles.run.bind(this)}"
      ></media-picker-button>
    </div>`;
  }

  /**
   * Renders the files from api
   */
  private renderFiles() {
    return this.getApiFiles.render({
      pending: () => this.renderLoading(),
      complete: (files: any) => {
        if (files.length === 0) {
          return html`<empty-list></empty-list>`;
        }

        const filteredFiles = this.filterFiles(files);

        return this.viewMode === ViewMode.card
          ? html`
              <card-view
                .files=${filteredFiles}
                @file-selected="${this.selectFileCustomEventHandler.bind(this)}"
                @file-unselected="${this.unselectFileCustomEventHandler.bind(
                  this
                )}"
              ></card-view>
            `
          : html`<list-view .files=${filteredFiles}></list-view>`;
      },
      error: () => this.renderError(),
    });
  }

  render() {
    return html`
      <div class="gallery">
        <header class="gallery-header">
          <span></span>
          <div class="actions">
            <media-search @search="${this.handleSearch}"></media-search>
            <media-picker-button
              .icon="${this.viewMode === ViewMode.list ? listIcon : gridIcon}"
              @click="${this.toggleViewMode.bind(this)}"
            >
            </media-picker-button>
            <media-picker-button
              variant="primary"
              .icon="${uploadIcon}"
              label="Upload"
              @click="${this.openUploadDialog.bind(this)}"
            ></media-picker-button>
          </div>
        </header>
        <div class="gallery-body">${this.renderFiles()}</div>
      </div>

      <media-picker-upload-file
        @close="${this.closeUploadDialog.bind(this)}"
        @files="${this.handleFileChange.bind(this)}"
        @files-uploaded="${this.handleFileUploaded.bind(this)}"
      ></media-picker-upload-file>
    `;
  }
}
